import * as React from 'react'
import { FlowExperimentProvider } from '../../hooks/useFlowExperiment/context/FlowExperimentProvider'
import { FlowFeatureProvider } from '../../hooks/useFlowFeature/context/FlowFeatureProvider'
import { OptimizelyContext } from '../../hooks/useOptimizelyContext/context'
import { OptimizelyWebId } from '../OptimizelyWebId'
import { OptimizelyWebScript } from '../OptimizelyWebScript'
import { ReactNode, useMemo } from 'react'
import { OptimizelyProvider as ReactOptimizelyProvider } from '@optimizely/react-sdk'
import { useIsMounted } from '@dtx-company/true-common/src/hooks/useIsMounted'
import { useOptimizelyClient } from '../../hooks/useOptimizelyClient'
import { useOptimizelyContext } from '../../hooks/useOptimizelyContext'
import { useOptimizelyWebEvents } from '../../hooks/useOptimizelyWebEvents'

export interface OptimizelyProviderProps {
  children: React.ReactNode
  sessionId: string
  datafile: string
  overrides?: Record<string, string>
  attributes?: Record<string, string>
  isFlowpage?: boolean
}

const InnerOptimizelyProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const isMounted = useIsMounted()
  const context = useOptimizelyContext()
  const optimizely = useOptimizelyClient()
  useOptimizelyWebEvents()
  if (!optimizely || !context) return <>{children}</>
  return (
    <ReactOptimizelyProvider
      key={'test-heap-id'}
      isServerSide={!isMounted()}
      optimizely={optimizely}
    >
      {process.env.NODE_ENV === 'test' && (
        // need a way to test if the ReactOptimizelyProvider renders or not but spying
        // on it causes this error to throw:
        // Error: Uncaught [TypeError: Cannot set properties of undefined (setting 'props')]
        // and nothing else visibily renders, so adding something to check on
        <div data-testid="react-optimizely-provider"></div>
      )}
      <OptimizelyWebId sessionId={context.sessionId} />
      <OptimizelyWebScript key="OptimizelyWebScript" />
      <FlowFeatureProvider>
        <FlowExperimentProvider>{children}</FlowExperimentProvider>
      </FlowFeatureProvider>
    </ReactOptimizelyProvider>
  )
}

export function OptimizelyProvider(props: OptimizelyProviderProps): JSX.Element {
  const { children, isFlowpage } = props
  const { sessionId, datafile, overrides, attributes } = props
  // Memoize so the context only changes when its values do to avoid issues like
  // optimizely resetting things back to their defaults when using autoUpdate.
  // (I don't think the context ever actually does change - just the user does)
  const context = useMemo(() => {
    return { sessionId, datafile, overrides, attributes }
  }, [sessionId, datafile, overrides, attributes])
  return isFlowpage ? (
    <>{children}</>
  ) : (
    <OptimizelyContext.Provider value={context}>
      <InnerOptimizelyProvider>{children}</InnerOptimizelyProvider>
    </OptimizelyContext.Provider>
  )
}
